<script setup lang="ts">
import ChartEvent from '@/components/data-visualization/charts/ChartEvent.vue'
import { onMounted, watch, computed, ref } from 'vue'
import * as XLSX from 'xlsx'
import KpiUserOpportunities from '@/components/features/kpis/KpiUserOpportunities.vue'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { useEventStore } from '@/stores/useEventStore'
import { storeToRefs } from 'pinia'
import YearFilter from '@/components/filters/YearFilter.vue'
import RankingTable from '@/components/common/ui/RankingTable.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import { useOrderStore } from '@/stores/useOrderStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import CommercialPipelinePreview from '@/components/features/dashboard/CommercialPipelinePreview.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'

const MIN_RANKING_YEAR = 2026

function defaultRankingYearMonth(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth() + 1
  if (y < MIN_RANKING_YEAR) return `${MIN_RANKING_YEAR}-01`
  return `${y}-${String(m).padStart(2, '0')}`
}

function defaultRankingYear(): number {
  return Math.max(MIN_RANKING_YEAR, new Date().getFullYear())
}

const opportunityStore = useOpportunityStore()
const eventStore = useEventStore()
const orderStore = useOrderStore()
const boostCoreStore = useBoostCorePermissionStore()

const hasSalesAndUnixo = computed(() => boostCoreStore.hasSalesAndUnixo)
const buIncludesUnixo = computed(() => boostCoreStore.buIncludesUnixo)
const isSalesManager = computed(() => boostCoreStore.include('SALES_MANAGER'))

const { filters } = storeToRefs(opportunityStore)
const { rankings } = storeToRefs(orderStore)

const rankingPeriod = ref<'month' | 'year'>('month')
const rankingYearMonths = ref<string[]>([defaultRankingYearMonth()])
const rankingYear = ref<number>(defaultRankingYear())

const rankingMonthOptions = computed(() => {
  const opts: { id: string; value: string; label: string }[] = []
  const now = new Date()
  const endCap = new Date(now.getFullYear(), now.getMonth(), 1)
  let iter = new Date(MIN_RANKING_YEAR, 0, 1)
  while (iter <= endCap) {
    const y = iter.getFullYear()
    const mo = iter.getMonth() + 1
    const val = `${y}-${String(mo).padStart(2, '0')}`
    opts.push({
      id: val,
      value: val,
      label: iter.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    })
    iter.setMonth(iter.getMonth() + 1)
  }
  if (opts.length === 0) {
    const d = new Date(MIN_RANKING_YEAR, 0, 1)
    opts.push({
      id: '2026-01',
      value: '2026-01',
      label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    })
  }
  return opts.slice().reverse()
})

const rankingYearOptions = computed(() => {
  const yEnd = Math.max(new Date().getFullYear(), MIN_RANKING_YEAR)
  const list: { id: string; value: number; label: string }[] = []
  for (let y = yEnd; y >= MIN_RANKING_YEAR; y--) {
    list.push({ id: String(y), value: y, label: String(y) })
  }
  return list
})

async function loadRankings() {
  if (!boostCoreStore.buIncludesUnixo) return
  if (rankingPeriod.value === 'month') {
    if (rankingYearMonths.value.length === 0) return
    await orderStore.getRanking({ period: 'month', yearMonths: rankingYearMonths.value })
  } else {
    await orderStore.getRanking({ period: 'year', year: rankingYear.value })
  }
}

watch([rankingPeriod, rankingYearMonths, rankingYear], () => {
  void loadRankings()
}, { deep: true })

watch(rankingMonthOptions, (opts) => {
  const valid = new Set(opts.map((o) => o.value as string))
  const kept = rankingYearMonths.value.filter((v) => valid.has(v))
  if (kept.length !== rankingYearMonths.value.length) {
    rankingYearMonths.value = kept.length > 0 ? kept : [(opts[0]?.value as string) ?? defaultRankingYearMonth()]
  }
})

watch(rankingYearOptions, (opts) => {
  if (!opts.some((o) => o.value === rankingYear.value)) {
    rankingYear.value = (opts[0]?.value as number) ?? defaultRankingYear()
  }
})

const rankingPeriodLabel = computed(() => {
  if (rankingPeriod.value === 'year') return String(rankingYear.value)
  const labelByValue = new Map(rankingMonthOptions.value.map((o) => [o.value as string, o.label]))
  return rankingYearMonths.value.map((v) => labelByValue.get(v) ?? v).join(' + ')
})

function round2(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 100
}

function buildSheetRows(items: any[]): Array<Record<string, number | string>> {
  return items.map((item) => {
    const rec = (item.margeCompteurSubscription ?? 0) + (item.regulSubscription ?? 0)
    const os = (item.margeCompteurOneShot ?? 0) + (item.regulOneShot ?? 0)
    return {
      Rang: item.rank,
      Nom: item.name,
      'Récurrent': round2(rec),
      OneShot: round2(os),
      Total: round2(item.sum ?? 0),
    }
  })
}

function exportRankingsXlsx() {
  const sections: Array<{ sheet: string; items: any[] }> = [
    { sheet: 'Collaborateurs', items: rankings.value?.users ?? [] },
    { sheet: 'Agences', items: rankings.value?.agencies ?? [] },
    { sheet: 'Teams', items: rankings.value?.teams ?? [] },
    { sheet: 'Régions', items: rankings.value?.regions ?? [] },
  ]

  const workbook = XLSX.utils.book_new()
  const header = ['Rang', 'Nom', 'Récurrent', 'OneShot', 'Total']

  for (const section of sections) {
    const rows = buildSheetRows(section.items)
    const sheet = XLSX.utils.json_to_sheet(rows, { header })
    sheet['!cols'] = [{ wch: 6 }, { wch: 34 }, { wch: 14 }, { wch: 14 }, { wch: 14 }]
    XLSX.utils.book_append_sheet(workbook, sheet, section.sheet)
  }

  workbook.Props = {
    Title: `Classements ${rankingPeriodLabel.value}`,
    CreatedDate: new Date(),
  }

  const periodSlug =
    rankingPeriod.value === 'year'
      ? String(rankingYear.value)
      : rankingYearMonths.value.slice().sort().join('_')
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `classements_${periodSlug}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const getSelectedYear = (): number | undefined => filters.value.yearDeadLine?.year

onMounted(async () => {
  const year = getSelectedYear()
  await eventStore.getEventsCharts(year)
  if (boostCoreStore.buIncludesUnixo) await loadRankings()
})

watch(
  () => filters.value.yearDeadLine?.year,
  async (newYear, oldYear) => {
    if (newYear !== oldYear) {
      await eventStore.getEventsCharts(newYear)
    }
  },
)
</script>

<template>
  <div class="flex flex-col gap-4 p-4 sm:gap-8 sm:p-6 lg:gap-12 lg:p-8">
    <div class="flex w-full flex-col items-center justify-center gap-2 sm:items-start sm:justify-start">
      <div class="flex items-center gap-1">
        <p class="text-2xl font-bold">Bonjour {{ boostCoreStore.user ? `${boostCoreStore.user.firstName ?? ''} ${boostCoreStore.user.lastName ?? ''}`.trim() : '' }} !👋</p>
        <HelpButton />
      </div>
      <h3>Bienvenue</h3>
    </div>
    <div v-if="hasSalesAndUnixo" class="w-full">
      <CommercialPipelinePreview />
    </div>
    <div class="flex flex-col w-full items-center justify-center gap-6">
      <div class="flex flex-col w-full items-center justify-center">
        <h1 class="text-2xl font-bold">Analyse RDV</h1>
        <ChartEvent>
          <template #header>
            <div class="flex flex-row items-center gap-2">
              <span class="label-text font-semibold">Année</span>
              <YearFilter v-model="filters.yearDeadLine" class="w-32" />
            </div>
          </template>
        </ChartEvent>
      </div>
      <div class="flex flex-col w-full items-center justify-center gap-4">
        <h1 class="text-2xl font-bold">Analyse affaires</h1>
        <kpi-user-opportunities />
      </div>

      <div v-if="buIncludesUnixo" class="flex w-full flex-col gap-4">
        <div class="relative flex w-full min-w-0 min-h-13 items-end justify-center sm:min-h-17">
          <h1 class="text-center text-2xl font-bold">Classements</h1>
          <div class="absolute end-0 bottom-0 flex shrink-0 flex-nowrap items-end gap-2 max-sm:max-w-[55vw] max-sm:overflow-x-auto">
            <div class="join">
              <button
                type="button"
                class="btn btn-sm join-item"
                :class="rankingPeriod === 'month' ? 'btn-active' : ''"
                @click="rankingPeriod = 'month'"
              >
                Mois
              </button>
              <button
                type="button"
                class="btn btn-sm join-item"
                :class="rankingPeriod === 'year' ? 'btn-active' : ''"
                @click="rankingPeriod = 'year'"
              >
                Année
              </button>
            </div>
            <SelectInput
              v-if="rankingPeriod === 'month'"
              v-model="rankingYearMonths"
              label="Période"
              :options="rankingMonthOptions"
              placeholder="Mois"
              multiple
              class="min-w-[240px] max-w-md"
            />
            <SelectInput
              v-else
              v-model="rankingYear"
              label="Période"
              :options="rankingYearOptions"
              placeholder="Année"
              class="min-w-[140px] max-w-xs"
            />
            <button
              v-if="isSalesManager"
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="!rankings"
              @click="exportRankingsXlsx"
            >
              Exporter
            </button>
          </div>
        </div>
        <div class="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <RankingTable :items="rankings?.users || []" title="Marge Collaborateurs" class="" />
          <RankingTable :items="rankings?.agencies || []" title="Marge Agences" class="" />
          <RankingTable :items="rankings?.teams || []" title="Marge Teams" class="" />
          <RankingTable :items="rankings?.regions || []" title="Marge Régions" class="" />
        </div>
      </div>
    </div>
  </div>
</template>
